const TWO_PI = Math.PI * 2;
/** Wraps an angle to (-pi, pi]. */
export function wrapAngle(a) {
    let r = a % TWO_PI;
    if (r > Math.PI)
        r -= TWO_PI;
    else if (r <= -Math.PI)
        r += TWO_PI;
    return r;
}
/** Rotates `from` toward `to` along the shortest arc by at most `maxStep` radians. */
export function turnToward(from, to, maxStep) {
    const diff = wrapAngle(to - from);
    if (Math.abs(diff) <= maxStep)
        return wrapAngle(to);
    return wrapAngle(from + Math.sign(diff) * maxStep);
}
export function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
}
export function lerp(a, b, t) {
    return a + (b - a) * t;
}
export function degToRad(deg) {
    return (deg * Math.PI) / 180;
}
/** Moves the 2D vector (x, z) toward a target by at most `maxDelta`, without overshooting. */
export function moveToward2D(x, z, targetX, targetZ, maxDelta) {
    const dx = targetX - x;
    const dz = targetZ - z;
    const dist = Math.hypot(dx, dz);
    if (dist <= maxDelta || dist === 0)
        return { x: targetX, z: targetZ };
    const k = maxDelta / dist;
    return { x: x + dx * k, z: z + dz * k };
}
// --- vectors (plain objects; every function returns a fresh object unless documented) ---
export function vec3(x, y, z) {
    return { x, y, z };
}
export function vAdd(a, b) {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
export function vSub(a, b) {
    return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
export function vScale(a, k) {
    return { x: a.x * k, y: a.y * k, z: a.z * k };
}
/** a + b * k */
export function vAddScaled(a, b, k) {
    return { x: a.x + b.x * k, y: a.y + b.y * k, z: a.z + b.z * k };
}
export function vDot(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}
export function vLength(a) {
    return Math.hypot(a.x, a.y, a.z);
}
export function vDistance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}
/** Horizontal (XZ) distance. */
export function vDistanceXZ(a, b) {
    return Math.hypot(a.x - b.x, a.z - b.z);
}
/** Unit vector, or (0, 0, -1) for a zero vector so callers always get a usable direction. */
export function vNormalize(a) {
    const len = Math.hypot(a.x, a.y, a.z);
    if (len < 1e-12)
        return { x: 0, y: 0, z: -1 };
    return { x: a.x / len, y: a.y / len, z: a.z / len };
}
export function vLerp(a, b, t) {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}
export function vEquals(a, b, epsilon = 1e-9) {
    return (Math.abs(a.x - b.x) <= epsilon &&
        Math.abs(a.y - b.y) <= epsilon &&
        Math.abs(a.z - b.z) <= epsilon);
}
// --- aim and facing (forward at yaw 0 is -Z; pitch is positive upward) ---
/** Unit direction of the aim. */
export function aimDirection(yaw, pitch) {
    const c = Math.cos(pitch);
    return { x: -Math.sin(yaw) * c, y: Math.sin(pitch), z: -Math.cos(yaw) * c };
}
/** Unit horizontal forward vector for a yaw. */
export function forwardXZ(yaw) {
    return { x: -Math.sin(yaw), y: 0, z: -Math.cos(yaw) };
}
/** Unit horizontal right vector for a yaw. */
export function rightXZ(yaw) {
    return { x: Math.cos(yaw), y: 0, z: -Math.sin(yaw) };
}
/** Yaw whose forward vector points along the horizontal direction (dx, dz). */
export function yawOf(dx, dz) {
    return Math.atan2(-dx, -dz);
}
/** Pitch of a direction vector (positive upward). */
export function pitchOf(dx, dy, dz) {
    return Math.atan2(dy, Math.hypot(dx, dz));
}
/** Absolute angle in radians between two direction vectors. */
export function angleBetween(a, b) {
    const d = vDot(a, b) / (vLength(a) * vLength(b) || 1);
    return Math.acos(clamp(d, -1, 1));
}
//# sourceMappingURL=math.js.map